import { useState, useRef, useEffect } from 'react';
import { Settings, Truck, Clock, CalendarClock, AlertTriangle, Wrench, XCircle, CheckCircle2 } from 'lucide-react';
import { getDockDisplayStatus, DisplayStatus } from '../../lib/dock-utils';
import { dockMutations } from '../../lib/dockMutations';

/** Extended status used only within Dock Health — adds Reserved to the base DisplayStatus */
type EffectiveStatus = DisplayStatus | 'Reserved';

/** Minimal dock shape matching Dashboard.tsx Dock interface */
interface DockInfo {
    id: string;
    dockNumber: string;
    type?: string;
    status: string;
    temperatureZone?: string | null;
    handlingCapabilities?: string | null;
    exceptionReason?: string | null;
    exceptionNote?: string | null;
    exceptionOwnerUserId?: string | null;
    exceptionResolvedAt?: string | null;
}

interface OccupantInfo {
    dockId?: string;
    dock_id?: string;
    carrierName?: string;
    carrierMcNumber?: string;
    customerPoNumber?: string;
    loadId?: string;
    loadNumber?: string | number;
    checkInTime?: string;
    dockStartTime?: string;
    startAt?: string;
    status?: string;
}

/** Minimal shape for a future-scheduled appointment used to derive Reserved status */
interface ReservationInfo {
    dockId?: string;
    dock_id?: string;
    carrierName?: string;
    startAt?: string;
    status?: string;
}

interface DockHealthColumnProps {
    docks: DockInfo[];
    activeOccupants: OccupantInfo[];
    /** Today's approved/scheduled appointments — used to derive Reserved status */
    scheduledAppointments?: ReservationInfo[];
    onDockClick?: (dockId: string) => void;
    onStatusChange?: (dockId: string, newStatus: string) => void;
}

/**
 * Derive effective dock status by cross-referencing live occupancy AND future reservations.
 * Priority:
 *   1. If a live occupant is assigned to this dock → Occupied
 *   2. If raw dock status is Maintenance / Out of Service → that status
 *   3. If a future appointment (approved/scheduled) is assigned → Reserved
 *   4. Else → Available
 *
 * This ensures Dock Health cards stay in sync with the At Dock lane
 * and clearly distinguish physical presence from future assignments.
 */
function getEffectiveDockStatus(
    dock: DockInfo,
    occupantByDock: Map<string, OccupantInfo>,
    reservationByDock?: Map<string, ReservationInfo>
): EffectiveStatus {
    const hasLiveOccupant = occupantByDock.has(dock.id);
    const rawStatus = getDockDisplayStatus(dock as any);

    // 1. Live occupant always wins (this IS the ground truth)
    if (hasLiveOccupant) return 'Occupied';

    // 2. Maintenance / OOS locks take priority over reservations
    if (rawStatus === 'Maintenance' || rawStatus === 'Out of Service') return rawStatus;

    // 3. Future reservation (approved/scheduled appointment assigned to this dock)
    if (reservationByDock?.has(dock.id)) return 'Reserved';

    // 4. Fallback to raw status
    return rawStatus;
}

// ─── Gear Menu ──────────────────────────────────────────────────
function DockGearMenu({ dock, docks, effectiveStatus, onClose, onStatusChange }: { dock: DockInfo; docks: DockInfo[]; effectiveStatus: EffectiveStatus; onClose: () => void; onStatusChange?: (dockId: string, newStatus: string) => void }) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onClose();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    const isAvailable = effectiveStatus === 'Available';
    const isMaintenance = effectiveStatus === 'Maintenance';
    const isOOS = effectiveStatus === 'Out of Service';
    const isOccupied = effectiveStatus === 'Occupied';
    const isReserved = effectiveStatus === 'Reserved';

    const actions: { label: string; value: string; icon: React.ReactNode; color: string; show: boolean }[] = [
        { label: 'Set Available', value: 'Available', icon: <CheckCircle2 className="w-3.5 h-3.5" />, color: 'text-emerald-600 dark:text-emerald-400', show: !isAvailable && !isOccupied && !isReserved },
        { label: 'Set Maintenance', value: 'Maintenance', icon: <Wrench className="w-3.5 h-3.5" />, color: 'text-amber-600 dark:text-amber-400', show: !isMaintenance },
        { label: 'Set Out of Service', value: 'Out of Service', icon: <XCircle className="w-3.5 h-3.5" />, color: 'text-red-600 dark:text-red-400', show: !isOOS },
    ];

    const handleAction = async (value: string) => {
        try {
            if (onStatusChange) {
                onStatusChange(dock.id, value);
            } else {
                await dockMutations.updateDockStatus(dock.id, value, docks as any);
            }
        } catch { /* toast already handled by dockMutations */ }
        onClose();
    };

    return (
        <div
            ref={ref}
            className="absolute right-0 top-8 z-50 w-44 rounded-lg border border-border bg-popover shadow-xl py-1 animate-in fade-in-0 zoom-in-95 duration-100"
            onClick={(e) => e.stopPropagation()}
        >
            {actions.filter(a => a.show).map(a => (
                <button
                    key={a.value}
                    onClick={() => handleAction(a.value)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-medium hover:bg-accent transition-colors ${a.color}`}
                >
                    {a.icon}
                    {a.label}
                </button>
            ))}
            {actions.filter(a => a.show).length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-2">No actions available</p>
            )}
        </div>
    );
}

// ─── Compact Dock Card ──────────────────────────────────────────
function CompactDockCard({ dock, occupant, reservation, effectiveStatus, docks, onDockClick, onStatusChange }: {
    dock: DockInfo;
    occupant: OccupantInfo | undefined;
    reservation: ReservationInfo | undefined;
    effectiveStatus: EffectiveStatus;
    docks: DockInfo[];
    onDockClick?: (dockId: string) => void;
    onStatusChange?: (dockId: string, newStatus: string) => void;
}) {
    const [gearOpen, setGearOpen] = useState(false);

    const isOccupied = effectiveStatus === 'Occupied';
    const isReserved = effectiveStatus === 'Reserved';
    const isMaintenance = effectiveStatus === 'Maintenance';
    const isOOS = effectiveStatus === 'Out of Service';

    // Exception indicator
    const hasException = !dock.exceptionResolvedAt && (dock.exceptionReason || dock.exceptionNote || dock.exceptionOwnerUserId);

    // Dwell calculation
    const getDwell = () => {
        const checkIn = occupant?.checkInTime || occupant?.dockStartTime;
        if (!checkIn) return null;
        const diffMs = Date.now() - new Date(checkIn).getTime();
        if (isNaN(diffMs) || diffMs < 0) return null;
        const mins = Math.floor(diffMs / 60000);
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        const text = `${h}h ${m}m`;
        if (mins <= 120) return { text, cls: 'text-emerald-600 dark:text-emerald-400' };
        if (mins <= 240) return { text, cls: 'text-amber-600 dark:text-amber-400' };
        return { text, cls: 'text-red-600 dark:text-red-500 font-bold' };
    };

    const dwell = isOccupied ? getDwell() : null;

    // Border + bg based on status
    const borderCls = isOccupied
        ? 'border-l-blue-500 bg-blue-500/5 dark:bg-blue-500/10'
        : isReserved
            ? 'border-l-violet-400 bg-violet-500/5 dark:bg-violet-500/10'
            : isMaintenance
                ? 'border-l-amber-500 bg-amber-500/5 dark:bg-amber-500/10'
                : isOOS
                    ? 'border-l-red-500 bg-red-500/5 dark:bg-red-500/10'
                    : 'border-l-emerald-500 bg-card';

    const carrierLabel = occupant?.carrierName
        || (occupant?.carrierMcNumber ? `MC ${occupant.carrierMcNumber}` : null);
    const poLabel = occupant?.customerPoNumber || null;
    const loadLabel = occupant?.loadNumber ? `Load #${occupant.loadNumber}` : null;

    return (
        <div
            className={`relative rounded-lg border border-l-[3px] ${borderCls} border-border/60 p-2.5 transition-all hover:shadow-sm cursor-pointer group`}
            onClick={() => onDockClick?.(dock.id)}
        >
            {/* Row 1: Dock number + status badge */}
            <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-bold font-mono tracking-tight">{dock.dockNumber}</span>
                <div className="flex items-center gap-1.5">
                    {hasException && (
                        <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" title="Exception active" />
                    )}
                    <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                        isOccupied ? 'bg-cyan-50 border-cyan-200 text-cyan-700 dark:bg-cyan-500/10 dark:border-transparent dark:text-cyan-400' :
                        isReserved ? 'bg-violet-50 border-violet-200 text-violet-700 dark:bg-violet-500/10 dark:border-transparent dark:text-violet-400' :
                        isMaintenance ? 'bg-amber-50 border-amber-200 text-amber-700 dark:bg-amber-500/10 dark:border-transparent dark:text-amber-400' :
                        isOOS ? 'bg-red-50 border-red-200 text-red-700 dark:bg-red-500/10 dark:border-transparent dark:text-red-400' :
                        'bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:border-transparent dark:text-emerald-400'
                    }`}>
                        {effectiveStatus}
                    </span>
                </div>
            </div>

            {/* Row 2: Context — depends on effectiveStatus (single source of truth) */}
            {isOccupied ? (
                <div className="space-y-0.5 pr-6">
                    <div className="flex items-center gap-1 text-xs text-foreground">
                        <Truck className="w-3 h-3 text-primary shrink-0" />
                        <span className="truncate font-medium">{carrierLabel || 'Truck at dock'}</span>
                    </div>
                    {(poLabel || loadLabel) && (
                        <div className="text-[11px] text-muted-foreground truncate font-mono">
                            {poLabel ? `PO: ${poLabel}` : loadLabel}
                        </div>
                    )}
                    {dwell && (
                        <div className={`flex items-center gap-0.5 text-[11px] ${dwell.cls}`}>
                            <Clock className="w-2.5 h-2.5" />
                            <span>{dwell.text}</span>
                        </div>
                    )}
                </div>
            ) : isReserved ? (
                <div className="space-y-0.5 pr-6">
                    <div className="flex items-center gap-1 text-xs text-violet-600 dark:text-violet-400">
                        <CalendarClock className="w-3 h-3 shrink-0" />
                        <span className="truncate font-medium">{reservation?.carrierName || 'Reserved'}</span>
                    </div>
                    {reservation?.startAt && (
                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                            <Clock className="w-2.5 h-2.5" />
                            <span>{new Date(reservation.startAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
                        </div>
                    )}
                </div>
            ) : isMaintenance ? (
                <div className="flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                    <Wrench className="w-3 h-3" />
                    <span>Under Maintenance</span>
                </div>
            ) : isOOS ? (
                <div className="flex items-center gap-1 text-[11px] text-red-600 dark:text-red-400">
                    <AlertTriangle className="w-3 h-3" />
                    <span>Out of Service</span>
                </div>
            ) : (
                <div className="text-[11px] text-muted-foreground/50 italic">Available</div>
            )}

            {/* Gear button — bottom right */}
            <button
                onClick={(e) => { e.stopPropagation(); setGearOpen(!gearOpen); }}
                className="absolute bottom-1.5 right-1.5 p-1 rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-accent transition-colors"
                title="Dock actions"
            >
                <Settings className="w-3.5 h-3.5" />
            </button>

            {gearOpen && (
                <DockGearMenu dock={dock} docks={docks} effectiveStatus={effectiveStatus} onClose={() => setGearOpen(false)} onStatusChange={onStatusChange} />
            )}
        </div>
    );
}
// ─── Priority Sort for Preview Mode ─────────────────────────────
const PREVIEW_LIMIT = 6;
const PREVIEW_THRESHOLD = 8;

/**
 * Assign an operational priority score to a dock for preview sorting.
 * Lower score = higher priority = shown first in the preview.
 *
 *   0 — Maintenance / Out of Service  (ops needs to see offline capacity)
 *   1 — Occupied with dwell risk (>2h) or exception
 *   2 — Occupied (normal dwell)
 *   3 — Reserved
 *   4 — Available
 */
function getDockPriority(
    dock: DockInfo,
    effectiveStatus: EffectiveStatus,
    occupant: OccupantInfo | undefined
): number {
    if (effectiveStatus === 'Maintenance' || effectiveStatus === 'Out of Service') return 0;

    if (effectiveStatus === 'Occupied') {
        // Check dwell risk (>120 min = amber/red)
        const checkIn = occupant?.checkInTime || occupant?.dockStartTime;
        const hasDwellRisk = checkIn
            ? (Date.now() - new Date(checkIn).getTime()) > 120 * 60_000
            : false;

        // Check exception
        const hasException = !dock.exceptionResolvedAt && (
            dock.exceptionReason || dock.exceptionNote || dock.exceptionOwnerUserId
        );

        return (hasDwellRisk || hasException) ? 1 : 2;
    }

    if (effectiveStatus === 'Reserved') return 3;
    return 4; // Available
}

// ─── Main Column ────────────────────────────────────────────────
export function DockHealthColumn({ docks, activeOccupants, scheduledAppointments, onDockClick, onStatusChange }: DockHealthColumnProps) {
    // Sort docks by dock number
    const sorted = [...docks].sort((a, b) => {
        const numA = parseInt(a.dockNumber.replace(/\D/g, ''), 10) || 0;
        const numB = parseInt(b.dockNumber.replace(/\D/g, ''), 10) || 0;
        return numA - numB || a.dockNumber.localeCompare(b.dockNumber);
    });

    // Map occupant by dock ID (checked_in / in_progress)
    const occupantByDock = new Map<string, OccupantInfo>();
    for (const occ of activeOccupants) {
        const dockId = occ.dockId || occ.dock_id;
        if (dockId) occupantByDock.set(dockId, occ);
    }

    // Map reservation by dock ID (approved / scheduled — NOT already occupied)
    const reservationByDock = new Map<string, ReservationInfo>();
    const RESERVED_STATUSES = new Set(['approved', 'scheduled']);
    if (scheduledAppointments) {
        for (const appt of scheduledAppointments) {
            const dockId = appt.dockId || appt.dock_id;
            if (dockId && RESERVED_STATUSES.has(appt.status || '') && !occupantByDock.has(dockId)) {
                // Use earliest reservation per dock
                if (!reservationByDock.has(dockId)) reservationByDock.set(dockId, appt);
            }
        }
    }

    // Summary counts — using effective status
    const availCount = sorted.filter(d => getEffectiveDockStatus(d, occupantByDock, reservationByDock) === 'Available').length;
    const reservedCount = sorted.filter(d => getEffectiveDockStatus(d, occupantByDock, reservationByDock) === 'Reserved').length;
    const occupiedCount = sorted.filter(d => getEffectiveDockStatus(d, occupantByDock, reservationByDock) === 'Occupied').length;
    const offlineCount = sorted.filter(d => {
        const eff = getEffectiveDockStatus(d, occupantByDock, reservationByDock);
        return eff === 'Maintenance' || eff === 'Out of Service';
    }).length;

    // ─── Preview mode logic ─────────────────────────────────────
    const isPreviewMode = sorted.length > PREVIEW_THRESHOLD;

    // For preview mode: sort by operational priority, then take top N
    const displayDocks = isPreviewMode
        ? [...sorted].sort((a, b) => {
            const effA = getEffectiveDockStatus(a, occupantByDock, reservationByDock);
            const effB = getEffectiveDockStatus(b, occupantByDock, reservationByDock);
            const prioA = getDockPriority(a, effA, occupantByDock.get(a.id));
            const prioB = getDockPriority(b, effB, occupantByDock.get(b.id));
            if (prioA !== prioB) return prioA - prioB;
            // Within same priority, sort by dock number
            const numA = parseInt(a.dockNumber.replace(/\D/g, ''), 10) || 0;
            const numB = parseInt(b.dockNumber.replace(/\D/g, ''), 10) || 0;
            return numA - numB || a.dockNumber.localeCompare(b.dockNumber);
        }).slice(0, PREVIEW_LIMIT)
        : sorted;

    const hiddenCount = sorted.length - displayDocks.length;

    return (
        <div className="rounded-lg border-t-2 border-purple-400 dark:border-purple-500 border border-slate-200 dark:border-slate-700 bg-white/60 dark:bg-slate-800/40 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300">
                        Dock Health
                    </span>
                    {isPreviewMode && (
                        <span className="text-[10px] text-muted-foreground">
                            ({sorted.length} docks)
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" title="Available">
                        {availCount}
                    </span>
                    {reservedCount > 0 && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300" title="Reserved">
                            {reservedCount}
                        </span>
                    )}
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300" title="Occupied">
                        {occupiedCount}
                    </span>
                    {offlineCount > 0 && (
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" title="Offline">
                            {offlineCount}
                        </span>
                    )}
                    {isPreviewMode && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDockClick?.(sorted[0]?.id || ''); }}
                            className="ml-1 text-[10px] font-medium text-primary hover:text-primary/80 hover:underline transition-colors"
                        >
                            View Dock Board &rarr;
                        </button>
                    )}
                </div>
            </div>

            {/* Dock list — compact horizontal grid */}
            <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 p-2">
                {sorted.length === 0 ? (
                    <p className="text-xs text-muted-foreground/60 text-center py-3">No docks configured</p>
                ) : (
                    displayDocks.map(dock => (
                        <CompactDockCard
                            key={dock.id}
                            dock={dock}
                            occupant={occupantByDock.get(dock.id)}
                            reservation={reservationByDock.get(dock.id)}
                            effectiveStatus={getEffectiveDockStatus(dock, occupantByDock, reservationByDock)}
                            docks={docks}
                            onDockClick={onDockClick}
                            onStatusChange={onStatusChange}
                        />
                    ))
                )}
            </div>

            {/* Overflow link for preview mode */}
            {hiddenCount > 0 && (
                <div className="px-3 pb-2 -mt-0.5">
                    <button
                        onClick={() => onDockClick?.(sorted[0]?.id || '')}
                        className="w-full text-center text-[11px] font-medium text-muted-foreground hover:text-primary py-1.5 rounded-md hover:bg-accent/50 transition-colors"
                    >
                        +{hiddenCount} more dock{hiddenCount !== 1 ? 's' : ''} &mdash; View Dock Board
                    </button>
                </div>
            )}
        </div>
    );
}
